/**
 * Tiny CSV exporter for admin tables.
 *
 * - escapes embedded quotes, commas, newlines
 * - resolves nested keys via dot paths ("payer.id")
 * - kicks off a browser download (no extra dependency)
 */
export interface CsvColumn<T> {
  header: string
  /** Dot path or function returning the cell value. */
  field: string | ((row: T) => unknown)
}

function getByPath(obj: any, path: string): unknown {
  return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj)
}

function escape(value: unknown): string {
  if (value == null) return ''
  const s = typeof value === 'string' ? value : String(value)
  if (s.includes(',') || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

export function exportCsv<T>(
  filename: string,
  columns: CsvColumn<T>[],
  rows: T[]
): void {
  const header = columns.map((c) => escape(c.header)).join(',')
  const body = rows
    .map((row) =>
      columns
        .map((c) => {
          const v =
            typeof c.field === 'function' ? c.field(row) : getByPath(row, c.field)
          return escape(v)
        })
        .join(',')
    )
    .join('\n')
  const csv = `${header}\n${body}`

  // UTF-8 BOM so Excel opens non-ASCII correctly.
  const BOM = String.fromCharCode(0xfeff)
  const blob = new Blob([BOM, csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
